import type { AdapterMappingDefinition, AdapterMappingGuidance, AdapterMappingGuidanceRequirement } from "./types";

/** Where an extension mapping keeps its Extension JSON Template. */
export const EXTENSION_TEMPLATE_PATH = "data";

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Whether a template (a string or JSON) reads `column` through a placeholder: `{{column}}`, with pipes
 * (`{{column | date …}}`) or in an expression (`{{column == Hour ? hours : days}}`).
 */
export const templateReadsColumn = (template: unknown, column: string): boolean => {
  if (template === undefined || template === null) return false;
  const text = typeof template === "string" ? template : JSON.stringify(template);
  return new RegExp(`{{\\s*${escapeRegExp(column)}(?![A-Za-z0-9_])`).test(text);
};

/** A quoted header (`"approved"`) is a fixed value rather than a CSV column. */
const isLiteralHeader = (header: string): boolean => /^(["']).*\1$/.test(header.trim());

const hasExtensionTemplate = (mapping: AdapterMappingDefinition): boolean => {
  const template = (mapping.data as { data?: unknown } | undefined)?.data;
  return Boolean(template && typeof template === "object" && Object.keys(template).length > 0);
};

/**
 * An extension mapping with an Extension JSON Template reads its CSV columns through the template's placeholders; the
 * importer ignores its columns list. Its column guidance becomes a check that the template reads the column, set up in
 * the template, and fixed values (quoted headers) aren't columns, so their guidance is dropped.
 */
export const withExtensionTemplateColumns = (mapping: AdapterMappingDefinition): AdapterMappingDefinition => {
  if (mapping.type !== "entities" || !hasExtensionTemplate(mapping) || !mapping.guidance?.some((item) => item.column)) {
    return mapping;
  }
  return {
    ...mapping,
    guidance: mapping.guidance.flatMap((item): AdapterMappingGuidance[] => {
      if (!item.column) return [item];
      const header = String(item.column.header ?? "").trim();
      if (!header || isLiteralHeader(header)) return [];
      const { column: _column, ...rest } = item;
      const requirement: AdapterMappingGuidanceRequirement = {
        path: EXTENSION_TEMPLATE_PATH,
        operator: "templateColumn",
        value: header,
      };
      return [{
        ...rest,
        label: item.label ?? header,
        fileColumns: [header],
        targetPath: EXTENSION_TEMPLATE_PATH,
        requirements: [requirement],
      }];
    }),
  };
};
